import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useEffect, useState } from "react";
import {
  convertToCurrencyFormat,
  stockUnitOfMeasureOptions,
  isReadOnlyUser,
} from "../../../shared/utility";
import SelectMenu from "../../controls/SelectMenu";
import OrderSubtable1 from "./subtables/OrderSubtable1";
import OrderItemPriceSplitsTable from "./OrderItemPriceSplitsTable";
import TableLoadingSkeleton from "../../shared/TableLoadingSkeleton";
import CellSaveStatusIndicator from "../../controls/inputs/CellSaveStatusIndicator";
import { useCellSaveState } from "../../../hooks/useCellSaveState";
import CopyableIdentifier from "../../controls/CopyableIdentifier";

export default function OrderProductsTable(props) {
  const {
    loading,
    values,
    onItemEditComplete,
    rowDetails,
    fetchRowDetails,
    loadingRows,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    selectedItems,
    setSelectedItems,
    canEditSplits,
    setEmptyModelOpen,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
  } = props;

  const [expandedRows, setExpandedRows] = useState(null);
  const [pendingUnitTypes, setPendingUnitTypes] = useState({});
  const { getSaveStatus, saveCell } = useCellSaveState();

  const unitTypeCellKey = (rowData) =>
    `order:${rowData.id}:unit_type`;

  const clearPendingUnitType = (rowId) => {
    setPendingUnitTypes((current) => {
      if (!(rowId in current)) return current;
      const next = { ...current };
      delete next[rowId];
      return next;
    });
  };

  useEffect(() => {
    setPendingUnitTypes((current) => {
      const pendingIds = Object.keys(current);
      if (pendingIds.length === 0) return current;

      let changed = false;
      const next = { ...current };
      pendingIds.forEach((id) => {
        const item = values.items?.find(
          (row) => String(row.id) === String(id),
        );
        if (!item || Number(item.unit_type) === Number(current[id])) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [values.items]);

  const allowExpansion = () => {
    return true;
  };

  const rowExpansionTemplate = (data) => {
    const hasSplits = (data.price_splits || []).length > 0;

    return (
      <div>
        <OrderSubtable1
          data={data}
          loadingRows={loadingRows}
          rowDetails={rowDetails}
          fetchSubRowDetails={fetchSubRowDetails}
          subRowDetails={subRowDetails}
          subLoadingRows={subLoadingRows}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
        />
        {(hasSplits || canEditSplits) && (
          <OrderItemPriceSplitsTable
            orderItem={data}
            setEmptyModelOpen={setEmptyModelOpen}
            onCreatePriceSplit={onCreatePriceSplit}
            onUpdatePriceSplit={onUpdatePriceSplit}
            onDeletePriceSplit={onDeletePriceSplit}
            canEditSplits={canEditSplits}
          />
        )}
      </div>
    );
  };

  const updateUnitType = async (rowData, rowIndex, unitType) => {
    const normalizedUnitType = Number(unitType);
    const cellKey = unitTypeCellKey(rowData);
    if (
      ![1, 2, 3].includes(normalizedUnitType) ||
      normalizedUnitType === Number(rowData.unit_type) ||
      getSaveStatus(cellKey) === "saving"
    ) {
      return;
    }

    const quantity =
      normalizedUnitType === 2
        ? Number(rowData.total_in_lbs)
        : normalizedUnitType === 3
          ? Number(rowData.total_in_pieces)
          : Number(rowData.total_in_kg);
    const unitCost = Number(rowData.unit_cost);
    const newData = {
      ...rowData,
      unit_type: normalizedUnitType,
      total_cost:
        Number.isFinite(unitCost) && Number.isFinite(quantity)
          ? unitCost * quantity
          : rowData.total_cost,
    };

    setPendingUnitTypes((current) => ({
      ...current,
      [rowData.id]: normalizedUnitType,
    }));

    try {
      const result = await saveCell(cellKey, () =>
        onItemEditComplete(newData, rowIndex),
      );
      if (!result?.success) {
        clearPendingUnitType(rowData.id);
      }
      return result;
    } catch (error) {
      clearPendingUnitType(rowData.id);
      throw error;
    }
  };

  const productDetailsBody = (rowData) => {
    return (
      <p>
        <span className="text-gray-900 font-semibold">
          {rowData.product_name}
        </span>
        <br />
        <span className="text-gray-700">PLU:{rowData.plu}</span>
      </p>
    );
  };

  const lotNumberBody = (rowData) => (
    <CopyableIdentifier
      value={rowData.lot_number}
      description="lot number"
    />
  );

  const totalCostBody = (rowData) => {
    return <p>{convertToCurrencyFormat(rowData.total_cost)}</p>;
  };

  const totalCartonsParent = (rowData) => {
    return (
      <div>
        <p>{rowData.total_cartons} cartons</p>
      </div>
    );
  };

  const totalShippedWeightParent = (rowData) => {
    return (
      <div>
        <p>{rowData.total_in_kg}kg</p>
        <p>{rowData.total_in_lbs}lbs</p>
        <p>{rowData.total_in_pieces} pieces</p>
      </div>
    );
  };

  const unitTypeLabel = (unitType) => {
    switch (unitType) {
      case 1:
        return "kg";

      case 2:
        return "lbs";

      case 3:
        return "pieces";

      default:
        return "N/A";
    }
  };

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!rowDetails[rowData.unique_id]) {
      fetchRowDetails(rowData);
    }
  };
  const isReadOnly = isReadOnlyUser();

  const rowHasMultipleSplits = (rowData) =>
    (rowData.price_splits || []).length > 1;

  const unitTypeBody = (rowData, options) => {
    if (isReadOnly || rowHasMultipleSplits(rowData)) {
      return unitTypeLabel(rowData.unit_type);
    }

    const saveStatus = getSaveStatus(unitTypeCellKey(rowData));
    const displayedUnitType =
      pendingUnitTypes[rowData.id] ?? rowData.unit_type;

    return (
      <div className="flex min-w-[8.25rem] max-w-[10.25rem] items-center gap-1">
        <div className="min-w-0 flex-1">
          <SelectMenu
            ariaLabel={`Unit Type for ${rowData.product_name}`}
            disabled={saveStatus === "saving"}
            handleInputChange={(event) =>
              updateUnitType(
                rowData,
                options.rowIndex,
                event.target.value.value,
              )
            }
            menuPortalTarget={document.body}
            name={`unit_type_${rowData.id}`}
            options={stockUnitOfMeasureOptions}
            value={stockUnitOfMeasureOptions.find(
              (option) => option.value === Number(displayedUnitType),
            )}
          />
        </div>
        <CellSaveStatusIndicator status={saveStatus} />
      </div>
    );
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[900px] py-2">
        {loading ? (
          <TableLoadingSkeleton rows={5} columns={4} />
        ) : (
          <div className="overflow-hidden">
            <DataTable
              value={values.items}
              expandedRows={expandedRows}
              onRowToggle={(e) => {
                setExpandedRows(e.data);
              }}
              onRowExpand={onRowExpand}
              rowExpansionTemplate={rowExpansionTemplate}
              dataKey="id"
              tableClassName="min-w-full bg-white divide-y divide-gray-300 create-order-inventory-table-parent"
              cellClassName="p-2 bg-white border-1-bottom-gray text-sm"
            >
              <Column header="Product" body={productDetailsBody} />
              <Column header="Lot Number" body={lotNumberBody} />

              <Column
                header="Shipped Cartons"
                body={totalCartonsParent}
                style={{ textAlign: "right" }}
              />

              <Column
                header="Shipped amount"
                body={totalShippedWeightParent}
                style={{ textAlign: "right" }}
              />

              <Column
                header="Total Price"
                field="total_cost"
                style={{ textAlign: "right" }}
                body={totalCostBody}
              />

              <Column
                header="Unit Type"
                field="unit_type"
                headerClassName="order-products-unit-type-column"
                bodyClassName="order-products-unit-type-column"
                headerStyle={{ minWidth: "8rem", textAlign: "left" }}
                bodyStyle={{ textAlign: "left" }}
                body={unitTypeBody}
              />
              <Column expander={allowExpansion} style={{ width: "6%" }} />
            </DataTable>
          </div>
        )}
        <div className="w-full text-right mt-4">
          <p className="font-bold">
            Subtotal: {convertToCurrencyFormat(values.subtotal_dollar_amount)}
          </p>
          <p className="font-bold">
            Taxes: {convertToCurrencyFormat(values.tax_amount)}
          </p>
          <p className="font-bold">
            Total: {values.currency?.label || ""}{" "}
            {convertToCurrencyFormat(values.total_dollar_amount)}
          </p>
        </div>
      </div>
    </div>
  );
}
